/** WHERE A VISITOR CAME FROM, KEPT AND CARRIED — the one source (2026-09-30).
 *
 *  THE ROOT FILE. Its copies are apps/marketing/lib/first-touch.ts and apps/form/src/first-touch.ts, and since 2026-10-17
 *  (TW-0330) one in each of the other four demos' apps, apps/link-page/src/, apps/deposit-page/src/, apps/booking-page/src/
 *  and apps/website/src/, byte for byte; each app's own test fails when its copy drifts (the email-suggest.ts shape). Edit
 *  this file, copy it over the six, commit all seven:
 *
 *    for d in apps/marketing/lib apps/form/src apps/link-page/src apps/deposit-page/src apps/booking-page/src apps/website/src; do cp first-touch.ts "$d/"; done
 *
 *  ## Why it exists
 *
 *  Chris, 2026-09-30, after a shop that paid that evening turned out to have come from an ad its row never named:
 *  "if we can be less blind, to all forms of attribution, that's great." And of the one row nobody could explain:
 *  ""i can't tell" means we've failed you and can do better."
 *
 *  Measured that night over the month's 222 signups: 41 rows could not say where they came from (17 "direct", 24 "a
 *  Facebook link with no ad tags"), and the paying shops among them were 11 of 39. Three leaks made most of it:
 *
 *    1. A FIRST TOUCH LIVES IN ONE BROWSER'S STORAGE FOR ONE ORIGIN. A person who taps an ad inside Instagram and
 *       finishes in Chrome arrives knowing nothing (24th Street Tattoo: the row said "the demo"). A person who goes
 *       from www.waivers.tattoo to www.L.tattoo is on another origin with empty storage (Immortal Dragon Tattoo Studio,
 *       47 seconds after landing from "Nothing to install": the row said "www.waivers.tattoo", which is a hop and not
 *       a source). And the demo is a third origin that kept nothing at all.
 *    2. A LATER VISIT WITH A BARE REFERRER REPLACED AN EARLIER ONE THAT NAMED AN AD. Three shops clicked an ad, came
 *       back days later through an untagged Facebook link and signed up; the row kept the second visit and the ad was
 *       gone (the browser's own journey still had it, and no report read that).
 *    3. NOTHING KEPT MORE THAN ONE TOUCH. The row carried one record, so every rule about which touch wins threw the
 *       other away.
 *
 *  ## What this module is
 *
 *  THE TOUCHES: every landing that can say where it came from (a campaign tag, a click id, a referrer that is not
 *  ours) is kept, oldest first, up to TOUCHES_MAX for TOUCH_LIFE_MS, and the signup posts the list beside the one
 *  record it always posted. Rows are cheap; which touch gets the credit is the READ's decision, made with all of them
 *  in hand (scripts/lib_beacon.py best_source).
 *
 *  THE CARRY: the touches ride a link from one of our origins to another as `?wt=<id>` since TW-0532 (the id is below; until
 *  then, and still read wherever it arrives, `?wt_ft=<token>`), and inside an in-app
 *  browser (Instagram's, Facebook's) they ride the page's own address, so "open in my browser" hands them to the
 *  phone's own. The receiving page takes them as touches marked `carried`, uses the newest as the visit's own record
 *  when the visit knows nothing itself, and (outside an in-app browser) takes the token back out of the address.
 *
 *  A token is the touches and nothing else: tags, a click id, the referring site, the landing path, the time. No id of
 *  a person, no journey beyond CARRY_MAX landings. Someone who copies an in-app address to a friend hands the friend's
 *  browser a carried touch; it is marked `carried`, it is believed for CARRY_LIFE_MS from the ORIGINAL landing, and a
 *  read can say so. That was weighed against 41 rows a month that say nothing.
 *
 *  Pure: no storage, no window. The glue is each app's own (apps/marketing/lib/attribution.ts, apps/form/src/carry.ts),
 *  and the four other demos share one, the root demo-carry.ts, copied beside this file in each of their apps. Every demo
 *  hands on what it was handed since 2026-10-17 (TW-0330, the link audit's leftover): until then the demo form alone did,
 *  on its two wordmark links, and the link page's, the deposit page's, the booking page's and the website's demos drew every
 *  link with nothing on it (on 2026-09-30, 4 of the week's 128 demo page loads were the deposit page's and the link page's).
 *
 *  ## Every other parameter a landing arrived with (2026-10-01)
 *
 *  Chris, 2026-10-01, about tracking: "cool and yeah re: tracking i'd just make sure we're saving query params if we're
 *  not, and expecting those for analysis. thanks." We were not. A landing kept the eleven TOUCH_KEYS, the referrer, the
 *  path and the time, and threw every other parameter on its address away: Instagram's `igsh`, Google's `srsltid`,
 *  `gad_source` and `gbraid`, Microsoft's `msclkid`, TikTok's `ttclid`, a mail tool's `mc_cid`. A landing whose only tag
 *  was one of those, with no referrer, recorded NOTHING and read as "direct".
 *
 *  So a landing also keeps `query` (landingQuery): every other parameter on the address as ONE sanitized query string,
 *  in the address's order. And a landing whose only word is a tracker's parameter now says where it came from
 *  (saysWhere, looksTagged). `query` is a field of the record like any other and it is NOT one of the carried keys: it
 *  never rides a token, so a landing that says where only by its `query` rides no link either (encodeCarry).
 *
 *  ## TikTok's click id, a field of its own (2026-10-20, TW-0410)
 *
 *  Chris, that morning, with TikTok's pixel id in hand: "likely worth getting that implemented and pushed and deployed as i
 *  might run a TikTok campaign". TikTok adds `ttclid` to a landing's address each time one of its advertisements is tapped,
 *  the way Google adds `gclid`, and until that day it lived in `query`, which never rides a link: a TikTok ad opened inside
 *  TikTok's own browser and finished in the phone's lost its click. So `ttclid` is the twelfth of TOUCH_KEYS: a field of the
 *  record, carried on a link with the rest (the click ids ride the newest touch only, encodeCarry), on the signup row's
 *  `attribution` and `touches`, where the site's TikTok events read it (apps/marketing/lib/tiktok.ts tiktokMatchFor) and the
 *  reads credit TikTok by it (scripts/lib_beacon.py says, source_label).
 *
 *  A SECRET IN A ROW IS WORSE THAN A MISSING TAG, so the rule is written as what is LEFT OUT, and anything in doubt is:
 *  our own parameters, a name that says the value is a secret or a person, a value shaped like an email or a phone
 *  number, a name that is not a plain short word, and every parameter on a page whose links are one person's
 *  (PERSONAL_PATHS). The reads that expect them are scripts/lib_beacon.py (which mirrors looksTagged's list, held to
 *  this file by a test) and `./run.sh landing-params`.
 */

export type Touch = Record<string, string>;

/** The query parameters that say where a visit came from. `ref` is our own hand-written link mark. */
export const TOUCH_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_placement',
  'utm_network',
  'utm_adset',
  'fbclid',
  'gclid',
  'ttclid',
  'ref',
] as const;

/** The advertising platforms' click ids among TOUCH_KEYS: Meta's, Google's and (since 2026-10-20, TW-0410) TikTok's. Each is the
 *  long part of a token, so a link carries them on its newest touch only, and a token too long for an address loses them first
 *  (encodeCarry). */
export const CLICK_ID_KEYS: readonly string[] = ['fbclid', 'gclid', 'ttclid'];

/** What a carried touch may hold: the tags, and the three facts every landing record has. `query` is deliberately not
 *  one of them: every other parameter of an address stays on the row it landed on and never rides a link. */
const CARRIED_KEYS: readonly string[] = [...TOUCH_KEYS, 'referrer', 'landing', 'at'];

/** How many keys one record keeps, here and at every server door that is handed one (the signup, the draft, the contact
 *  form: apps/marketing/lib/first-touch.test.ts holds the doors to this number). The twelve tags, referrer, landing, at,
 *  query, via, carried and the signup's variant are 19 (the tags were eleven, and the whole 18, until TikTok's `ttclid` joined
 *  them on 2026-10-20). It was 16 until `query` joined them (2026-10-01). */
export const RECORD_KEYS_MAX = 20;

export const CARRY_PARAM = 'wt_ft';

// ── THE TINY IDENTIFIER (TW-0532, 2026-11-03) ──────────────────────────────────────────────────────────────────────────
// Chris, 2026-10-27, of the site's demo button: "demo opens in a new tab with a wildly long link that needs fixing" · "the URL
// itself is off" · "that can be a tiny identifier". The token above rode every link whole: three touches of his own browser made
// a 723-character `?wt_ft=`, and the link grew with every visit. So a link now carries `?wt=<10 characters>`, an id minted in the
// browser at the press (never on a page view), and the token itself is kept server-side under the id for CARRY_ID_LIFE_MS
// (apps/marketing/app/api/carry/route.ts, lib/carry-store.ts). The receiving page asks for the token back and takes it exactly as
// it took `wt_ft`, and the signup door resolves every id the browser kept as a net, so the row's `attribution` and `touches`, and
// every read of them (`./run.sh funnel`, `visitor-journey`), read the same story. The id is random: it carries nothing and
// reveals nothing, least of all anything a person typed. A link minted before this keeps working for its life: `wt_ft` is still
// read wherever it arrives, and a demo hands on whichever of the two it was opened with.

/** The short id's name on an address. Ours, so a landing's kept query never holds it (keepsParam). */
export const CARRY_ID_PARAM = 'wt';
/** The id's length: 10 characters of the URL-safe base64 alphabet, 60 random bits. */
export const CARRY_ID_LENGTH = 10;
/** How long the token stays resolvable under its id: the life of a touch (TOUCH_LIFE_MS), and the store's TTL. */
export const CARRY_ID_LIFE_MS = 30 * 24 * 60 * 60 * 1000;
const CARRY_ID = /^[A-Za-z0-9_-]{10}$/;
const ID_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Is this a carry id's shape: exactly CARRY_ID_LENGTH URL-safe characters. A token is never one (the shortest list of one
 *  touch is far longer), so a value says by its shape alone which of the two it is. */
export function isCarryId(value: unknown): value is string {
  return typeof value === 'string' && CARRY_ID.test(value);
}

/** A fresh id: CARRY_ID_LENGTH characters, each from 6 random bits. `random` is the crypto source (a test hands its own). */
export function newCarryId(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): string {
  const bytes = random(CARRY_ID_LENGTH);
  let out = '';
  // an index loop: the form's build targets an ES version that cannot iterate a Uint8Array
  for (let i = 0; i < CARRY_ID_LENGTH; i++) out += ID_ALPHABET[(bytes[i] ?? 0) & 63];
  return out;
}

/** The parameter a carried value rides as: `wt` for an id, `wt_ft` for a token (the old shape). */
export function carryParamFor(value: string): string {
  return isCarryId(value) ? CARRY_ID_PARAM : CARRY_PARAM;
}

/** The carry id on an address's query, or '' when there is none or it is not an id's shape. */
export function carriedIdIn(search: string | null | undefined): string {
  try {
    const v = new URLSearchParams(`${search ?? ''}`).get(CARRY_ID_PARAM);
    return isCarryId(v) ? v : '';
  } catch {
    return '';
  }
}

/** Does this URL carry a value of ours already, in either shape. */
function carriesOne(url: URL): boolean {
  return url.searchParams.has(CARRY_PARAM) || url.searchParams.has(CARRY_ID_PARAM);
}
/** How many touches a browser keeps: the first, and the newest five. */
export const TOUCHES_MAX = 6;
/** How long a touch stays on the list. A shop that saw an ad three weeks before it signed up still says so. */
export const TOUCH_LIFE_MS = 30 * 24 * 60 * 60 * 1000;
/** How long a carried touch stands in as the visit's own record: the site's first-touch life (attribution.ts FIRST_TOUCH_TTL_MS). */
export const CARRY_LIFE_MS = 7 * 24 * 60 * 60 * 1000;
/** How many touches a link carries: the first and the newest two. An address is not a row. */
export const CARRY_MAX = 3;
/** A second landing with the same tags inside this long is the same arrival (a reload, a back button), not a new touch. */
export const SAME_ARRIVAL_MS = 30 * 60 * 1000;
/** A token longer than this is cut down before it rides an address (the click id is the long part). */
export const TOKEN_MAX = 1800;

/** Our own hosts: every product site, demo, shop page and the shop app. An arrival from one is a hop, not a source.
 *  websites.tattoo joined both lists on 2026-10-16, found by the link audit (scripts/every-link-goes-where-its-words-say.mjs):
 *  from its door's build on 2026-10-08 a hop from it read as a source and no touch rode a link to its landing or its demo.
 *  texting.tattoo joined both lists the same day, the day its landing was built (docs/TEXTING.md): a hop from it is never a
 *  source, and a link to its landing carries the touches. emailing.tattoo joined both on 2026-10-18, the day its landing was
 *  built (docs/EMAILING.md), for the same two reasons. conventions.tattoo joined both on 2026-10-19, the evening it joined the
 *  family row (TW-0375; the marketing gate read red on dev until it did, mended by TW-0382's leg). ads.tattoo joined both on
 *  2026-10-20, the day its landing was built (docs/ADS.md), emailing.tattoo's two reasons. flashes.tattoo joined both on 2026-10-21,
 *  the day its gallery was built (TW-0415): its one button goes to www.waivers.tattoo/start, so the touch that brought a person to
 *  the gallery rides that link and the hop is never a source. bio.tattoo joined both on 2026-11-02 (TW-0591), the night it became
 *  the Links product's name and its site, the family row's address for it: a hop from www.bio.tattoo is never a source, and a link
 *  to it carries the touches, as www.l.tattoo's, which serves the same landing, always has. stencils.tattoo joined both the same
 *  night, the day its landing was built (TW-0589, docs/STENCILS.md), ads.tattoo's two reasons. apps/marketing/lib/
 *  first-touch.test.ts reads the family row and the site's demos and holds both lists to them. */
const OWN_SUFFIXES = ['.waivers.tattoo', '.deposits.tattoo', '.l.tattoo', '.bookings.tattoo', '.websites.tattoo', '.texting.tattoo', '.emailing.tattoo', '.conventions.tattoo', '.ads.tattoo', '.flashes.tattoo', '.bio.tattoo', '.stencils.tattoo'];
export function isOwnHost(host: string): boolean {
  const h = `${host ?? ''}`.trim().toLowerCase();
  return OWN_SUFFIXES.some((s) => h === s.slice(1) || h.endsWith(s));
}

export function hostOf(url: string): string {
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return '';
  }
}

/** The hosts a link may carry the touches TO: our sites and our demos, where a prospect is still a prospect. Never a
 *  shop's own page (a client's page takes nothing of ours in its address) and never the shop app (the signup is done). */
export const CARRY_HOSTS: readonly string[] = [
  'www.waivers.tattoo',
  'waivers.tattoo',
  'www.deposits.tattoo',
  'deposits.tattoo',
  'www.l.tattoo',
  'l.tattoo',
  'www.bookings.tattoo',
  'bookings.tattoo',
  'www.websites.tattoo',
  'websites.tattoo',
  'www.texting.tattoo',
  'texting.tattoo',
  'www.emailing.tattoo',
  'emailing.tattoo',
  'www.conventions.tattoo',
  'conventions.tattoo',
  'www.ads.tattoo',
  'ads.tattoo',
  'www.flashes.tattoo',
  'flashes.tattoo',
  'www.bio.tattoo',
  'bio.tattoo',
  'www.stencils.tattoo',
  'stencils.tattoo',
  'demo.waivers.tattoo',
  'demo.deposits.tattoo',
  'demo.l.tattoo',
  'demo.bookings.tattoo',
  'demo.websites.tattoo',
];

// ── EVERY OTHER PARAMETER A LANDING ARRIVED WITH (2026-10-01; the header has the why) ────────────────────────────────

/** The whole kept string, at most. A server door keeps a record's value to 300 characters, so `query` is never cut there. */
export const QUERY_MAX = 300;
/** How many parameters one landing keeps beside its tags. */
export const QUERY_PARAMS_MAX = 12;
/** One value, at most. A longer one is cut here, before the whole is measured. */
export const QUERY_VALUE_MAX = 120;

/** A name worth keeping is a plain short word. */
const QUERY_NAME = /^[A-Za-z0-9_.-]{1,40}$/;
/** A name that says its value is a secret or a person, wherever in the name it says so… */
const SECRET_IN_NAME = /token|secret|passw|auth|session|signature|email|phone|address/i;
/** …and the short names that say it whole: a signature, a sign-in code, a key (the welcome page's owner key), the
 *  unsubscribe link's `t`, a signed payload's `d`, a person's name. Compared in lower case. */
const SECRET_NAMES: readonly string[] = ['sig', 'code', 'key', 't', 'd', 'name'];
/** A parameter whose value is a number by its own definition, so ten digits there are an id and never a phone: Google's
 *  campaign number, which is eleven. It is one of TAGGED_NAMES below, and without this line it could never arrive. */
const NUMBER_ID_NAMES: readonly string[] = ['gad_campaignid'];

/** THE PAGES WHOSE LINKS ARE ONE PERSON'S: the ones we mail or hand to one owner or one client, whose addresses carry
 *  that person's key (`/welcome/<shop>?key=`, `/shop/<shop>?token=`, `/unsubscribe?s=&t=`). Nothing on such an address
 *  is kept, whatever it is called. A path that STARTS with one of these, in any case. (`/read` was on this list for
 *  an hour on 2026-10-01, on the design's guess, and came off before it shipped: it is the Instagram bio link's PUBLIC
 *  page, the one landing where a platform's own parameters arrive most, and the builder said so.) */
export const PERSONAL_PATHS: readonly string[] = ['/unsubscribe', '/r/', '/welcome', '/activate', '/activated', '/placard', '/sign-in', '/shop', '/api'];

/** Is this one parameter kept? Each line is one reason it is not. */
function keepsParam(name: string, value: string): boolean {
  if (!QUERY_NAME.test(name)) return false;                                  // not a plain short word
  const n = name.toLowerCase();
  if ((TOUCH_KEYS as readonly string[]).includes(name)) return false;        // a tag: it is its own field already
  if (n === CARRY_ID_PARAM || n.startsWith('wt_')) return false;            // ours: the carry id and token, the internal mark
  if (SECRET_IN_NAME.test(name) || SECRET_NAMES.includes(n)) return false;   // the name says a secret, or a person
  if (/@|%40/i.test(value)) return false;                                    // an email, plain or still escaped
  // a phone number: ten or more digits and no letter. A tracker's id has letters; the one that has none is named above.
  if (!NUMBER_ID_NAMES.includes(n) && !/[a-z]/i.test(value) && value.replace(/\D/g, '').length >= 10) return false;
  return true;
}

/** Every other parameter on a landing's address, as ONE query string in the address's order: what is left after the
 *  tags (their own fields), our own `wt_` parameters, anything that looks like a secret or a person (keepsParam), and
 *  everything on a page whose links are one person's (PERSONAL_PATHS). Each value at most QUERY_VALUE_MAX characters,
 *  at most QUERY_PARAMS_MAX parameters, the whole at most QUERY_MAX: one that does not fit is dropped from the end,
 *  never cut. '' when nothing is left. A tag in another spelling (`UTM_SOURCE`) is not one of the eleven fields, so it
 *  is kept here instead of lost. Pure. */
export function landingQuery(search: string, pathname: string): string {
  const path = `${pathname ?? ''}`.toLowerCase();
  if (PERSONAL_PATHS.some((p) => path.startsWith(p))) return '';
  const kept: [string, string][] = [];
  new URLSearchParams(`${search ?? ''}`).forEach((value, name) => {
    if (kept.length < QUERY_PARAMS_MAX && keepsParam(name, value)) kept.push([name, value.slice(0, QUERY_VALUE_MAX)]);
  });
  while (kept.length) {
    const out = new URLSearchParams();
    for (const [name, value] of kept) out.append(name, value);
    const text = out.toString();
    if (text.length <= QUERY_MAX) return text;
    kept.pop();
  }
  return '';
}

/** A record's `query`, held to landingQuery's rule AGAIN where a server is handed it. The browser applies the rule at the
 *  address; a door does not take a browser's word for what it left out (the terms box is held on the phone and on the
 *  server for the same reason). Over anything landingQuery wrote it changes nothing; a query with nothing left is taken
 *  off, and so is one on a page whose links are one person's (the record's own `landing`). Mutates and returns. */
export function queryHeldAtTheDoor<T extends Record<string, string>>(record: T): T {
  const r = record as Record<string, string>;
  if (typeof r.query !== 'string' || !r.query) return record;
  const kept = landingQuery(`?${r.query}`, typeof r.landing === 'string' ? r.landing : '');
  if (kept) r.query = kept;
  else delete r.query;
  return record;
}

/** A parameter NAME that is a tracker's: a click id (it ends `clid`: ttclid, twclid, dclid, yclid), a campaign tag
 *  beyond the eleven (it starts `utm_`: utm_id, utm_source_platform), or one of TAGGED_NAMES: Microsoft's click id
 *  (which ends `clkid`), Google's search-result and ad marks, Instagram's share marks, Mailchimp's, LinkedIn's. Our own
 *  functional parameters (for, state, type, lang, v) never match. scripts/lib_beacon.py spells these three once more for
 *  the reads, and scripts/tests/test_lib_beacon.py reads THIS file and compares, so the two cannot drift. */
export const TAGGED_SUFFIX = 'clid';
export const TAGGED_PREFIX = 'utm_';
export const TAGGED_NAMES = ['msclkid', 'srsltid', 'gad_source', 'gad_campaignid', 'gbraid', 'wbraid', 'igsh', 'igshid', 'mc_cid', 'mc_eid', 'li_fat_id'] as const;

function taggedName(name: string): boolean {
  const n = name.toLowerCase();
  return n.endsWith(TAGGED_SUFFIX) || n.startsWith(TAGGED_PREFIX) || (TAGGED_NAMES as readonly string[]).includes(n);
}

/** The trackers' names in a kept query, each once, in its order. [] for none, and for anything that is not a query. */
export function taggedNames(query: string | null | undefined): string[] {
  const out: string[] = [];
  if (!query || typeof query !== 'string') return out;
  new URLSearchParams(query).forEach((_value, name) => {
    if (taggedName(name) && !out.includes(name)) out.push(name);
  });
  return out;
}

/** Does a kept query carry a tracker's parameter? Then the landing says where it came from with no tag and no referrer. */
export function looksTagged(query: string | null | undefined): boolean {
  return taggedNames(query).length > 0;
}

/** Does a record say where the visit came FROM: a tag, a click id, a referrer that is not one of ours, or (since
 *  2026-10-01) a tracker's parameter among the others its address carried. */
export function saysWhere(t: Touch | undefined | null): boolean {
  if (!t) return false;
  if (TOUCH_KEYS.some((k) => t[k])) return true;
  const host = t.referrer ? hostOf(t.referrer) : '';
  if (host && !isOwnHost(host)) return true;
  return looksTagged(t.query);
}

function when(t: Touch): number {
  return Date.parse(t.at ?? '');
}

/** How far ahead of this clock a touch's time may be and still be believed: a link opened on another device, or a
 *  phone whose clock runs fast. */
const AHEAD_MS = 10 * 60 * 1000;

/** A touch that is on the clock: it has a time, the time is not ahead of now, and it is inside `life`. */
function alive(t: Touch, now: number, life: number): boolean {
  const at = when(t);
  return !Number.isNaN(at) && at - now < AHEAD_MS && now - at < life;
}

/** The same arrival seen twice: every tag equal and the same referring host. The time and the landing path are not
 *  part of what an arrival IS. */
export function sameTouch(a: Touch, b: Touch): boolean {
  for (const k of TOUCH_KEYS) if ((a[k] ?? '') !== (b[k] ?? '')) return false;
  return hostOf(a.referrer ?? '') === hostOf(b.referrer ?? '');
}

/** A list by its SHAPE alone, for a server that is handed one: flat maps of strings under plain keys (RECORD_KEYS_MAX at most, 300
 *  characters each), each saying where it came from and carrying a time that parses; oldest first; never more than
 *  TOUCHES_MAX. No clock is consulted: the times are the visitor's phone's, and a server that judged them by its own
 *  would drop the newest touch of every phone running a few minutes fast. Anything else is dropped, never repaired. */
export function cleanTouches(list: unknown): Touch[] {
  if (!Array.isArray(list)) return [];
  const out: Touch[] = [];
  for (const raw of list.slice(0, 4 * TOUCHES_MAX)) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const t: Touch = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === 'string' && v && /^[a-z_]{1,32}$/.test(k)) t[k] = v.slice(0, 300);
      if (Object.keys(t).length >= RECORD_KEYS_MAX) break;
    }
    queryHeldAtTheDoor(t);
    if (saysWhere(t) && !Number.isNaN(when(t))) out.push(t);
  }
  out.sort((a, b) => when(a) - when(b));
  return cap(out);
}

/** A list as storage or a token handed it back, in the BROWSER, where the clock is the one the times were written by:
 *  cleanTouches, and only what is still on the clock. */
export function freshTouches(list: unknown, now: number): Touch[] {
  return cap(cleanTouches(Array.isArray(list) ? list.filter((t) => t && typeof t === 'object' && !Array.isArray(t) && alive(t as Touch, now, TOUCH_LIFE_MS)) : []));
}

/** The first touch and the newest ones: the first is who found us, the newest is who brought the signup. */
function cap(list: Touch[], max = TOUCHES_MAX): Touch[] {
  return list.length <= max ? list : [list[0], ...list.slice(list.length - (max - 1))];
}

/** The list after one more landing. `found` is what THIS page load can see; a load that cannot say where it came
 *  from, or that repeats the newest touch inside SAME_ARRIVAL_MS, leaves the list as it was (pruned). */
export function withTouch(list: Touch[], found: Touch | undefined, now: number): Touch[] {
  const kept = freshTouches(list, now);
  if (!found || !saysWhere(found) || !alive(found, now, TOUCH_LIFE_MS)) return kept;
  const last = kept[kept.length - 1];
  if (last && sameTouch(last, found) && Math.abs(when(found) - when(last)) < SAME_ARRIVAL_MS) return kept;
  return cap([...kept, found]);
}

/** Two lists as one, oldest first, an arrival seen in both kept once (the kept one is the earlier, with its marks). */
export function mergeTouches(a: Touch[], b: Touch[], now: number): Touch[] {
  const all = [...freshTouches(a, now), ...freshTouches(b, now)].sort((x, y) => when(x) - when(y));
  const out: Touch[] = [];
  for (const t of all) {
    if (out.some((o) => sameTouch(o, t) && Math.abs(when(o) - when(t)) < SAME_ARRIVAL_MS)) continue;
    out.push(t);
  }
  return cap(out);
}

/** UTF-8 through base64, URL-safe. `unescape(encodeURIComponent(s))` is the text as its own bytes, one character each,
 *  which is what btoa takes; encoding the percent-escaped text itself tripled every quote and colon of the JSON, and a
 *  touch with long tags then fit no address at all. */
const toBase64Url = (s: string) => btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const fromBase64Url = (s: string) => decodeURIComponent(escape(atob(s.replace(/-/g, '+').replace(/_/g, '/'))));

/** What a link carries: the touches' own facts (never `carried`, `via` or anything a page added), at most CARRY_MAX of
 *  them, as one URL-safe token. '' when there is nothing that says where. A token over TOKEN_MAX is cut: first to the
 *  newest touch alone, then without its click id. */
export function encodeCarry(list: Touch[], now: number): string {
  const strip = (t: Touch, clickId: boolean): Touch => {
    const o: Touch = {};
    for (const k of CARRIED_KEYS) if (t[k] && (clickId || !CLICK_ID_KEYS.includes(k))) o[k] = t[k];
    return o;
  };
  // a landing that says where only by its `query` has nothing a token may hold, so it is left out BEFORE the cut to
  // CARRY_MAX: it can take no place on a link, and it can never be the "newest" that takes the click id off the ad
  // behind it
  const fresh = cap(freshTouches(list, now).filter((t) => saysWhere(strip(t, true))), CARRY_MAX);
  if (!fresh.length) return '';
  const last = fresh.length - 1;
  // the click id rides on the newest touch only: it is the long part, and the newest is the one a conversion is matched on
  const tries: Touch[][] = [fresh.map((t, i) => strip(t, i === last)), [strip(fresh[last], true)], [strip(fresh[last], false)]];
  for (const attempt of tries) {
    const token = toBase64Url(JSON.stringify(attempt));
    if (token.length <= TOKEN_MAX) return token;
  }
  return '';
}

/** A token back into touches, each marked `carried`. [] for anything unreadable, stale or not ours to believe: a token
 *  is typed into an address by whoever likes, so it is held to the same rule as storage (freshTouches) and to the
 *  carried keys alone. */
export function decodeCarry(token: string | null | undefined, now: number): Touch[] {
  if (!token || token.length > TOKEN_MAX || !/^[A-Za-z0-9_-]+$/.test(token)) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(fromBase64Url(token));
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const only = parsed.slice(0, CARRY_MAX).map((raw) => {
    const t: Record<string, unknown> = {};
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      for (const k of CARRIED_KEYS) if (k in (raw as Record<string, unknown>)) t[k] = (raw as Record<string, unknown>)[k];
    }
    return t;
  });
  return freshTouches(only, now).map((t) => ({ ...t, carried: '1' }));
}

/** The carried touch that stands in as the visit's own record when the visit knows nothing itself: the newest one
 *  still inside CARRY_LIFE_MS of its ORIGINAL landing. */
export function carriedRecord(carried: Touch[], now: number): Touch | undefined {
  for (let i = carried.length - 1; i >= 0; i--) if (alive(carried[i], now, CARRY_LIFE_MS)) return carried[i];
  return undefined;
}

/** A browser inside another app, where "open in my browser" starts a second browser that knows nothing. Measured on
 *  this site's own rows: Instagram and Facebook are all but a handful of them; the rest are named for the day they are. */
export function inAppBrowser(ua: string | null | undefined): boolean {
  return /Instagram|FBAN|FBAV|FB_IAB|FB4A|BytedanceWebview|musical_ly|Snapchat|LinkedInApp|Pinterest|Line\//.test(`${ua ?? ''}`);
}

/** A link to another of our origins, with the token: the href to use, or undefined to leave it alone (not ours to
 *  carry to, the same origin, no token, or it carries one already). */
export function carryOnLink(href: string, token: string, hereHost: string): string | undefined {
  if (!token) return undefined;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return undefined;
  }
  const host = url.host.toLowerCase();
  if ((url.protocol !== 'https:' && url.protocol !== 'http:') || !CARRY_HOSTS.includes(host) || host === `${hereHost ?? ''}`.toLowerCase()) return undefined;
  if (carriesOne(url)) return undefined;
  url.searchParams.set(carryParamFor(token), token);
  return url.toString();
}

// ── WHAT A DEMO HANDS ON (2026-09-30 on the demo form; every demo since 2026-10-17, TW-0330) ──────────────────────────
// A demo is a shop's page (the demo shop's form, link page, deposit page, booking page and website), and a shop's page keeps
// nothing: no storage, no beacon of where a person came from. So a demo hands on what it was HANDED: the token it was opened
// with, or, opened with none, one made from its own arrival, written onto its links to our sites and demos. The first two
// rules were the demo form's own (apps/form/src/carry.ts `arrival` and `demoCarryToken`) and moved here unchanged the day the
// other four demos began handing it on, so the five read one rule; each app's glue only reads the page and writes the links.

/** What one load of a DEMO can say about where it came from, as a touch: the tags on its address and a referrer that is
 *  not one of ours. undefined when it can say nothing (a typed address, a bookmark, a hop from our own site that carried
 *  no token). `landing` is the demo's host: a demo is one page as far as where a person came from goes. */
export function demoArrival(search: string, referrer: string, host: string, now: number): Touch | undefined {
  const q = new URLSearchParams(search);
  const t: Touch = {};
  for (const k of TOUCH_KEYS) {
    const v = q.get(k);
    if (v) t[k] = v.slice(0, 200);
  }
  const from = referrer ? hostOf(referrer) : '';
  if (from && !isOwnHost(from)) t.referrer = referrer.slice(0, 300);
  if (!Object.keys(t).length) return undefined;
  return { ...t, landing: host.slice(0, 120), at: new Date(now).toISOString() };
}

/** The token a demo's links leave with: the one the page was opened with (held to the rule again, decodeCarry), else one
 *  made from its own arrival, else ''. */
export function demoCarryToken(search: string, referrer: string, host: string, now: number): string {
  // an id it was opened with is handed on as it came (TW-0532): a demo keeps nothing and resolves nothing, and the id names the
  // same touches on every hop
  const id = carriedIdIn(search);
  if (id) return id;
  const carried = decodeCarry(new URLSearchParams(search).get(CARRY_PARAM), now);
  if (carried.length) return encodeCarry(carried, now);
  const own = demoArrival(search, referrer, host, now);
  return own ? encodeCarry([own], now) : '';
}

/** A link on a DEMO, with the token: carryOnLink's rule (another of our sites or demos), and also another page of the same
 *  demo, because a demo keeps nothing but its address: the website demo's Artists and Visit are pages of their own, and
 *  without the token on the way there the next page meets the visitor knowing nothing. Never the very page it is on (a
 *  place on it, or itself), never a shop's own page, and never another page of the SITE, which keeps its own touches on its
 *  own origin (attribution.ts). `here` is the page's own address. undefined = leave the link as drawn. */
export function carryOnDemoLink(href: string, token: string, here: string): string | undefined {
  if (!token) return undefined;
  let page: URL;
  let url: URL;
  try {
    page = new URL(here);
    url = new URL(href);
  } catch {
    return undefined;
  }
  const across = carryOnLink(href, token, page.host);
  if (across !== undefined) return across;
  const host = url.host.toLowerCase();
  if ((url.protocol !== 'https:' && url.protocol !== 'http:') || host !== page.host.toLowerCase()) return undefined;
  if (!host.startsWith('demo.') || !CARRY_HOSTS.includes(host)) return undefined;
  if (url.pathname === page.pathname && url.search === page.search) return undefined;
  if (carriesOne(url)) return undefined;
  url.searchParams.set(carryParamFor(token), token);
  return url.toString();
}

/** The address this page should show: WITH the token inside an in-app browser (so "open in my browser" hands it on),
 *  WITHOUT it anywhere else (so a link copied from a real browser is clean). undefined = it is already right. */
export function addressWithCarry(address: string, token: string, inApp: boolean): string | undefined {
  let url: URL;
  try {
    url = new URL(address);
  } catch {
    return undefined;
  }
  const param = carryParamFor(token);
  const other = param === CARRY_PARAM ? CARRY_ID_PARAM : CARRY_PARAM;
  if (inApp && token) {
    if (url.searchParams.get(param) === token && !url.searchParams.has(other)) return undefined;
    url.searchParams.delete(other);
    url.searchParams.set(param, token);
    return url.toString();
  }
  if (!carriesOne(url)) return undefined;
  url.searchParams.delete(CARRY_PARAM);
  url.searchParams.delete(CARRY_ID_PARAM);
  return url.toString();
}
